// apps/web/src/features/commercial-proposals/components/CommercialProposalPreview.tsx
//
// @aiPurpose INTERNAL customer-facing preview of a proposal (#1217),
//   rendered from the already-loaded owner/Admin detail (content +
//   backend `calculation`). It builds the SAME `CommercialProposalPublicDto`
//   the public projection ships (via the shared `buildCommercialProposalSheetModel`)
//   and renders it through the SAME `CommercialProposalCustomerSheet`, so the
//   operator sees a pixel-identical document to the customer.
// @aiSecurity this preview MUST NOT call any public endpoint and MUST NOT
//   increment public-view analytics — it renders purely from the in-memory
//   detail response, and the bound photo is fetched through the AUTHENTICATED
//   presigned-URL endpoint, never a public route. There is intentionally NO
//   public-endpoint data fetching here.
import { useTranslation } from 'react-i18next';
import {
  buildCommercialProposalSheetModel,
  type CommercialProposalCalculationOutput,
  type CommercialProposalContent,
} from '@magimp/shared';

import { CommercialProposalCustomerSheet } from '../sheet/CommercialProposalCustomerSheet.js';
import { SavedProductPhoto } from './SavedProductPhoto.js';

export function CommercialProposalPreview({
  content,
  calculation,
  proposalId,
}: {
  content: CommercialProposalContent;
  calculation: CommercialProposalCalculationOutput;
  proposalId: string;
}) {
  const { t } = useTranslation('commercialProposals');
  const dto = buildCommercialProposalSheetModel(content, calculation, {
    hasPhoto: content.photoObjectKey != null,
  });
  return (
    <div data-testid="commercial-proposals-preview">
      <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: 'var(--fg-1)' }}>
        {t('drawer.previewTitle')}
      </h3>
      <CommercialProposalCustomerSheet
        dto={dto}
        photoSlot={content.photoObjectKey ? <SavedProductPhoto proposalId={proposalId} /> : null}
      />
    </div>
  );
}
